import { useState } from 'react';
import {
  Bug as BugIcon, 
  Code as CodeIcon, 
  RefreshCcw as RefreshCcwIcon, 
  Shield as ShieldIcon, 
  BarChart as BarChartIcon, 
  Globe as GlobeIcon
} from 'lucide-react';
import Modal from './Modal';

// Service data
const services = [
  {
    id: 'bug-fixes',
    title: 'Bug Fixes',
    icon: <BugIcon className="h-8 w-8 text-blue-600" />,
    description: 'Quick identification and resolution of bugs and errors in your website.',
    modalContent: {
      title: 'Website Bug Fixing Services',
      description: 'Our team of experts quickly identifies and resolves bugs and errors that are affecting your website\'s functionality, ensuring a smooth user experience.',
      features: [
        'Comprehensive bug identification and diagnosis',
        'Quick resolution of critical errors',
        'Cross-browser compatibility fixes',
        'Mobile responsiveness issues',
        'Form submission and validation errors',
        'JavaScript and CSS debugging',
        'Performance optimization',
        'Fixing funnels and connections',
        'Resolving checkout issues'
      ],
      cta: {
        text: 'Fix My Website Issues',
        link: '/dashboard/settings?tab=products'
      }
    }
  },
  {
    id: 'content-updates',
    title: 'Content Updates',
    icon: <RefreshCcwIcon className="h-8 w-8 text-blue-600" />,
    description: 'Keep your content fresh with text updates, image replacements, and typo corrections.',
    modalContent: {
      title: 'Website Content Management',
      description: 'Our team can help you keep your website content up-to-date, professional, and error-free with quick edits and updates.',
      features: [
        'Text content editing and updating',
        'Image replacement and optimization',
        'Typo and grammar fixes',
        'Layout adjustments',
        'New page creation',
        'Content organization improvements',
        'Product listings and catalog updates'
      ],
      cta: {
        text: 'Update My Website Content',
        link: '/dashboard/settings?tab=products'
      }
    }
  },
  {
    id: 'feature-implementation',
    title: 'Feature Implementation',
    icon: <CodeIcon className="h-8 w-8 text-blue-600" />,
    description: 'Add new features and functionality to enhance your website.',
    modalContent: {
      title: 'Website Feature Implementation',
      description: 'Enhance your website with new features and functionality that improve user experience and add value to your online presence.',
      features: [
        'Custom contact forms and lead generation tools',
        'Interactive elements and animations',
        'E-commerce functionality',
        'User account management systems',
        'Search functionality and filters',
        'Social media integration',
        'Custom calculators and tools'
      ],
      cta: {
        text: 'Add New Features',
        link: '/contact'
      }
    }
  },
  {
    id: 'website-updates',
    title: 'Website Updates',
    icon: <RefreshCcwIcon className="h-8 w-8 text-blue-600" />,
    description: 'Keep your website content fresh and up-to-date with regular updates.',
    modalContent: {
      title: 'Website Content Updates',
      description: 'Maintain a fresh and relevant website with our content update services, ensuring your visitors always have access to the latest information.',
      features: [
        'Regular content refreshes',
        'Product and service information updates',
        'New page creation and integration',
        'Image and media updates',
        'Pricing and promotion updates',
        'Blog post additions',
        'Menu and navigation structure changes'
      ],
      cta: {
        text: 'Update My Website',
        link: '/contact'
      }
    }
  },
  {
    id: 'security-updates',
    title: 'Security Updates',
    icon: <ShieldIcon className="h-8 w-8 text-blue-600" />,
    description: 'Protect your website from vulnerabilities with security patches.',
    modalContent: {
      title: 'Website Security Services',
      description: 'Protect your website and user data with our comprehensive security services, keeping your online presence safe from threats and vulnerabilities.',
      features: [
        'Security vulnerability assessments',
        'SSL certificate installation and renewal',
        'Malware scanning and removal',
        'Security plugin updates and configuration',
        'Password protection implementation',
        'Form security and spam protection',
        'Regular security monitoring'
      ],
      cta: {
        text: 'Secure My Website',
        link: '/contact'
      }
    }
  },
  {
    id: 'performance-optimization',
    title: 'Performance Optimization',
    icon: <BarChartIcon className="h-8 w-8 text-blue-600" />,
    description: 'Improve website speed and performance for better user experience.',
    modalContent: {
      title: 'Website Performance Optimization',
      description: 'Enhance your website\'s speed and responsiveness with our performance optimization services, resulting in improved user experience and better search engine rankings.',
      features: [
        'Page load speed optimization',
        'Image compression and optimization',
        'Code minification and cleanup',
        'Browser caching implementation',
        'Database optimization',
        'Content Delivery Network (CDN) setup',
        'Mobile performance improvements'
      ],
      cta: {
        text: 'Speed Up My Website',
        link: '/contact'
      }
    }
  },
  {
    id: 'seo-improvements',
    title: 'SEO Improvements',
    icon: <GlobeIcon className="h-8 w-8 text-blue-600" />,
    description: 'Enhance your website visibility in search engine results.',
    modalContent: {
      title: 'SEO Enhancement Services',
      description: 'Improve your website\'s visibility in search engine results, driving more organic traffic and potential customers to your business.',
      features: [
        'On-page SEO optimization',
        'Meta tags and descriptions updates',
        'URL structure improvements',
        'Content optimization for keywords',
        'Image alt text optimization',
        'Internal linking structure',
        'Schema markup implementation'
      ],
      cta: {
        text: 'Improve My SEO',
        link: '/dashboard/settings?tab=products'
      }
    }
  },
  {
    id: 'analytics-setup',
    title: 'Analytics & Tracking',
    icon: <BarChartIcon className="h-8 w-8 text-blue-600" />,
    description: 'Install and configure analytics and marketing pixels for better insights.',
    modalContent: {
      title: 'Analytics & Tracking Setup',
      description: 'Get valuable insights about your website visitors and marketing campaigns with properly configured analytics and tracking tools.',
      features: [
        'Google Analytics setup and configuration',
        'Meta/Facebook Pixel installation',
        'Google Tag Manager implementation',
        'Conversion tracking setup',
        'Custom event tracking',
        'E-commerce tracking configuration',
        'Dashboard and reporting setup'
      ],
      cta: {
        text: 'Setup Analytics & Tracking',
        link: '/dashboard/settings?tab=products'
      }
    }
  }
];

// ServiceModal component
const ServiceDetailModal = ({ service, onClose }) => {
  if (!service) return null;
  
  const { modalContent } = service;
  
  return (
    <Modal 
      isOpen={true} 
      onClose={onClose} 
      title={modalContent.title}
    >
      <div className="mb-6">
        <p className="text-gray-700 mb-4">{modalContent.description}</p>
        
        <h4 className="font-medium text-gray-900 mb-2">We provide:</h4>
        <ul className="space-y-2">
          {modalContent.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg 
                className="h-5 w-5 text-green-500 mr-2 mt-0.5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="mt-6">
        <a 
          href="/dashboard/settings?tab=products"
          className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200"
        >
          {modalContent.cta.text}
        </a>
      </div>
    </Modal>
  );
};

// Common website issues
const commonIssues = [
  'Broken links and 404 errors',
  'Contact form not working',
  'Slow loading pages',
  'Mobile responsiveness issues',
  'Outdated plugins causing conflicts',
  'Security vulnerabilities',
  'Missing images or content'
];

// Services component
const Services = ({ activeServiceId: externalActiveServiceId, openServiceModal: externalOpenModal, closeServiceModal: externalCloseModal }) => {
  // Use internal state if no external state is provided
  const [activeServiceId, setActiveServiceId] = useState(null);
  
  // Determine which state to use
  const currentActiveServiceId = externalActiveServiceId !== undefined ? externalActiveServiceId : activeServiceId;
  
  // Use external or internal open modal function
  const openModal = (serviceId) => {
    if (externalOpenModal) {
      externalOpenModal(serviceId);
    } else {
      setActiveServiceId(serviceId);
    }
  };

  // Use external or internal close modal function
  const closeModal = () => {
    if (externalCloseModal) {
      externalCloseModal();
    } else {
      setActiveServiceId(null);
    }
  };

  return (
    <section id="services" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Support Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive solutions to help your website stay updated, secure, and performing at its best
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-primary/20 cursor-pointer animate-fadeIn"
              onClick={() => openModal(service.id)}
              style={{animationDelay: `${service.id * 0.1}s`}}
            >
              <div className="text-primary text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">{service.title}</h3>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-20 bg-white p-8 rounded-xl shadow-md border border-gray-100 animate-fadeIn">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Website Issues We Fix</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commonIssues.map((issue, index) => (
              <div key={index} className="flex items-center animate-fadeIn" style={{animationDelay: `${index * 0.05}s`}}>
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary mr-3">
                  ✓
                </div>
                <span className="text-gray-700">{issue}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a 
              href="/register"
              className="inline-block bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors shadow-sm hover:shadow-md"
            >
              Get Website Support
            </a>
          </div>
        </div>
      </div>
      
      {currentActiveServiceId && (
        <ServiceDetailModal 
          service={services.find(s => s.id === currentActiveServiceId)}
          onClose={closeModal}
        />
      )}
    </section>
  );
};

export default Services;